import React, { useEffect, useMemo, useState } from 'react';
import { ApiError, apiFetch } from '../api';
import { formatDateTime } from '../date';
import type { Tool } from '../types';
import { AiConfigPanel, SecurityPanel } from './ProfilePanels';
import { SharesPanel } from './SharesPanel';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth';
import { Modal } from '../components/Modal';
import { useMessageToast } from '../useMessageToast';
import { useDialog } from '../dialog';
import { AdminWorkspace } from './admin/AdminWorkspace';

type ResultItem = {
  id: string;
  toolId: string;
  toolName: string;
  renderText: string;
  createdAt: number;
};

type ExportItem = {
  id: string;
  toolId: string;
  toolName: string;
  resultId: string;
  createdAt: number;
};

function fmt(ts: number) {
  return formatDateTime(ts) || new Date(ts).toLocaleString();
}

function fmtOnline(seconds?: number | null) {
  const sec = Math.max(0, Math.floor(Number(seconds || 0)));
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0) return `${h}时${String(m).padStart(2, '0')}分${String(s).padStart(2, '0')}秒`;
  return `${m}分${String(s).padStart(2, '0')}秒`;
}

function downloadText(filename: string, text: string) {
  const blob = new Blob([text], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function ProfilePage() {
  const { confirmDelete, confirmSignOut } = useDialog();
  const location = useLocation();
  const navigate = useNavigate();
  const { refresh, logout } = useAuth();
  const [tools, setTools] = useState<Tool[]>([]);
  const [toolId, setToolId] = useState('');
  const [view, setView] = useState<'account' | 'results' | 'exports' | 'shares' | 'ai' | 'security' | 'admin'>('account');
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const [results, setResults] = useState<ResultItem[]>([]);
  const [exports, setExports] = useState<ExportItem[]>([]);
  const [user, setUser] = useState<{ id: string; email?: string; createdAt?: number; nickname?: string; onlineTotalSeconds?: number; lastActiveAt?: number | null } | null>(null);
  const [nicknameDraft, setNicknameDraft] = useState('');
  const [savingNickname, setSavingNickname] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [selectedResults, setSelectedResults] = useState<Record<string, boolean>>({});
  const [selectedExports, setSelectedExports] = useState<Record<string, boolean>>({});
  const [dataBulkOpen, setDataBulkOpen] = useState(false);
  const [dataBulkConfirm, setDataBulkConfirm] = useState('');
  const [dataBulkRunning, setDataBulkRunning] = useState(false);

  useMessageToast(msg, setMsg, 'profile-page');

  useEffect(() => {
    apiFetch<{ ok: boolean; tools: Tool[] }>('/api/tools', { method: 'GET' })
      .then((r) => setTools(r.tools || []))
      .catch(() => setTools([]));
  }, []);

  useEffect(() => {
    apiFetch<{ ok: boolean; user: { id: string; email?: string; createdAt?: number; nickname?: string; onlineTotalSeconds?: number; lastActiveAt?: number | null } }>('/api/auth/me', { method: 'GET' })
      .then((r) => setUser(r.user || null))
      .catch(() => setUser(null));
  }, []);

  useEffect(() => {
    if (!user?.id) {
      setIsAdmin(false);
      return;
    }
    apiFetch<{ ok: boolean }>('/api/admin/ping', { method: 'GET' })
      .then((r) => setIsAdmin(Boolean(r.ok)))
      .catch(() => setIsAdmin(false));
  }, [user?.id]);

  useEffect(() => {
    setNicknameDraft(String(user?.nickname || '').trim());
  }, [user?.nickname]);

  useEffect(() => {
    if (location.pathname.startsWith('/profile/admin/')) {
      if (isAdmin) setView('admin');
      else setView('account');
      return;
    }
    const tab = new URLSearchParams(location.search).get('tab');
    if (tab === 'admin' && !isAdmin) {
      setView('account');
      return;
    }
    if (tab === 'account' || tab === 'results' || tab === 'exports' || tab === 'shares' || tab === 'ai' || tab === 'security' || tab === 'admin') {
      setView(tab);
    }
  }, [location.pathname, location.search, isAdmin]);

  const query = useMemo(() => {
    const qs = new URLSearchParams();
    qs.set('page', String(page));
    qs.set('pageSize', String(pageSize));
    if (toolId) qs.set('toolId', toolId);
    return qs.toString();
  }, [page, pageSize, toolId]);

  const load = async () => {
    setMsg(null);
    if (view === 'exports') {
      const data = await apiFetch<{ ok: boolean; items: ExportItem[] }>(`/api/exports?${query}`, { method: 'GET' });
      setExports(data.items || []);
      setSelectedExports({});
    } else if (view === 'results') {
      const data = await apiFetch<{ ok: boolean; items: ResultItem[] }>(`/api/results?${query}`, { method: 'GET' });
      setResults(data.items || []);
      setSelectedResults({});
    }
  };

  useEffect(() => {
    if (view === 'account') return;
    load().catch(() => {
      setResults([]);
      setExports([]);
    });
  }, [view, query]);

  const reloadUser = async () => {
    try {
      const r = await apiFetch<{ ok: boolean; user: { id: string; email?: string; createdAt?: number; nickname?: string; onlineTotalSeconds?: number; lastActiveAt?: number | null } }>('/api/auth/me', { method: 'GET' });
      setUser(r.user || null);
    } catch {
      setUser(null);
    }
  };

  useEffect(() => {
    if (view !== 'account') return;
    reloadUser();
  }, [view]);

  return (
    <div className="container">
      <div className="card">
        <div className="card-inner">
          <div className="section-title">
            <h2>个人中心</h2>
            <span className="muted">账号信息 / 历史 / 导出 / 分享 / AI 配置 / 安全</span>
          </div>

          <div className="row" style={{ marginBottom: 12 }}>
            <button className={`pill ${view === 'account' ? 'active' : ''}`} onClick={() => navigate('/profile?tab=account', { replace: false })}>
              账号信息
            </button>
            <button className={`pill ${view === 'results' ? 'active' : ''}`} onClick={() => navigate('/profile?tab=results', { replace: false })}>
              历史记录
            </button>
            <button className={`pill ${view === 'exports' ? 'active' : ''}`} onClick={() => navigate('/profile?tab=exports', { replace: false })}>
              导出记录
            </button>
            <button className={`pill ${view === 'shares' ? 'active' : ''}`} onClick={() => navigate('/profile?tab=shares', { replace: false })}>
              分享管理
            </button>
            <button className={`pill ${view === 'ai' ? 'active' : ''}`} onClick={() => navigate('/profile?tab=ai', { replace: false })}>
              AI 配置
            </button>
            <button className={`pill ${view === 'security' ? 'active' : ''}`} onClick={() => navigate('/profile?tab=security', { replace: false })}>
              安全
            </button>
            {isAdmin ? (
              <button
                className={`pill ${view === 'admin' ? 'active' : ''}`}
                onClick={() => {
                  if (!isAdmin) return;
                  navigate('/profile/admin/users', { replace: false });
                }}
              >
                管理
              </button>
            ) : null}
            {view === 'results' || view === 'exports' ? (
              <>
                <select value={toolId} onChange={(e) => setToolId(e.target.value)} style={{ width: 180 }}>
                  <option value="">全部工具</option>
                  {tools.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
                <button className="btn secondary" onClick={() => load().catch(() => setMsg('刷新失败'))}>
                  刷新
                </button>
                <button
                  className="btn secondary"
                  type="button"
                  disabled={view === 'results' ? !results.length : !exports.length}
                  onClick={() => {
                    if (view === 'results') {
                      const allSelected = results.length > 0 && results.every((it) => selectedResults[it.id]);
                      const next: Record<string, boolean> = {};
                      for (const it of results) next[it.id] = !allSelected;
                      setSelectedResults(next);
                    } else {
                      const allSelected = exports.length > 0 && exports.every((it) => selectedExports[it.id]);
                      const next: Record<string, boolean> = {};
                      for (const it of exports) next[it.id] = !allSelected;
                      setSelectedExports(next);
                    }
                  }}
                >
                  全选本页
                </button>
                <button
                  className="btn secondary"
                  type="button"
                  disabled={
                    view === 'results'
                      ? Object.values(selectedResults).filter(Boolean).length === 0
                      : Object.values(selectedExports).filter(Boolean).length === 0
                  }
                  onClick={() => setDataBulkOpen(true)}
                >
                  批量删除（
                  {view === 'results'
                    ? Object.values(selectedResults).filter(Boolean).length
                    : Object.values(selectedExports).filter(Boolean).length}
                  ）
                </button>
              </>
            ) : view === 'shares' ? (
              null
            ) : view === 'ai' ? (
              null
            ) : view === 'security' ? (
              null
            ) : view === 'admin' ? (
              null
            ) : null}
          </div>

          {view === 'account' ? (
            <div style={{ display: 'grid', gap: 12 }}>
              <div className="tool">
                <div className="tool-top">
                  <div className="tool-name">账号信息</div>
                  <span className="badge">只读</span>
                </div>
                <div className="muted" style={{ lineHeight: 1.9 }}>
                  <div>注册邮箱：{user?.email || '-'}</div>
                  <div>注册时间：{user?.createdAt ? formatDateTime(user.createdAt) : '-'}</div>
                </div>
                <div
                  style={{
                    marginTop: 12,
                    borderRadius: 12,
                    border: '1px solid rgba(255,255,255,0.12)',
                    background: 'rgba(255,255,255,0.06)',
                    padding: '10px 12px'
                  }}
                >
                  <div style={{ fontWeight: 900 }}>累计在线时长：{fmtOnline(user?.onlineTotalSeconds)}</div>
                  <div className="muted" style={{ marginTop: 6 }}>
                    最后活跃时间：{user?.lastActiveAt ? fmt(user.lastActiveAt) : '-'}
                  </div>
                </div>
              </div>

              <div className="tool">
                <div className="tool-top">
                  <div className="tool-name">昵称</div>
                  <span className="badge">每 7 天可改 1 次</span>
                </div>
                <div className="field" style={{ marginTop: 10 }}>
                  <div className="muted">新昵称（2-16 字符，仅汉字/字母/数字/下划线）</div>
                  <input value={nicknameDraft} onChange={(e) => setNicknameDraft(e.target.value)} />
                </div>
                <div className="row" style={{ marginTop: 12, justifyContent: 'flex-end' }}>
                  <button
                    className="btn"
                    disabled={savingNickname}
                    onClick={async () => {
                      setSavingNickname(true);
                      setMsg(null);
                      try {
                        await apiFetch('/api/profile/nickname', { method: 'POST', body: JSON.stringify({ nickname: nicknameDraft }) });
                        await reloadUser();
                        await refresh();
                        setMsg('昵称已更新');
                      } catch (e) {
                        if (e instanceof ApiError) {
                          const c = (e.body as any)?.error;
                          if (c === 'INVALID_NICKNAME') setMsg('昵称不符合规则');
                          else if (c === 'TOO_FREQUENT') setMsg('修改过于频繁，请 7 天后再试');
                          else setMsg('保存失败，请稍后重试');
                        } else setMsg('保存失败');
                      } finally {
                        setSavingNickname(false);
                      }
                    }}
                  >
                    {savingNickname ? '保存中…' : '保存'}
                  </button>
                </div>
              </div>

              <div className="tool">
                <div className="tool-top">
                  <div className="tool-name">退出登录</div>
                  <span className="badge">谨慎操作</span>
                </div>
                <div className="muted" style={{ lineHeight: 1.8, marginTop: 10 }}>
                  为避免误触，退出按钮仅在个人中心展示。
                </div>
                <div className="row" style={{ marginTop: 12, justifyContent: 'flex-end' }}>
                  <button
                    className="btn danger"
                    onClick={async () => {
                      const ok = await confirmSignOut({ title: '退出登录', message: '确认退出登录？' });
                      if (!ok) return;
                      await logout();
                      try {
                        window.location.assign('/login');
                      } catch {
                      }
                    }}
                  >
                    退出登录
                  </button>
                </div>
              </div>
            </div>
          ) : view === 'shares' ? (
            <SharesPanel />
          ) : view === 'ai' ? (
            <AiConfigPanel />
          ) : view === 'security' ? (
            user?.email ? (
              <SecurityPanel email={user.email} />
            ) : (
              <div className="muted">加载中…</div>
            )
          ) : view === 'admin' ? (
            isAdmin ? (
              <AdminWorkspace isAdmin={isAdmin} />
            ) : (
              <div className="muted">无权限</div>
            )
          ) : view === 'results' ? (
            <div style={{ display: 'grid', gap: 12 }}>
              <div className="row" style={{ justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                <button
                  className="btn secondary"
                  type="button"
                  disabled={!results.length}
                  onClick={() => {
                    const allSelected = results.length > 0 && results.every((it) => selectedResults[it.id]);
                    const next: Record<string, boolean> = {};
                    for (const it of results) next[it.id] = !allSelected;
                    setSelectedResults(next);
                  }}
                >
                  {results.length > 0 && results.every((it) => selectedResults[it.id]) ? '取消全选' : '全选'}
                </button>
                <button
                  className="btn secondary"
                  type="button"
                  disabled={Object.values(selectedResults).filter(Boolean).length === 0}
                  onClick={() => setDataBulkOpen(true)}
                >
                  批量删除（{Object.values(selectedResults).filter(Boolean).length}）
                </button>
              </div>
              {results.map((it) => (
                <div key={it.id} className="tool">
                  <div className="tool-top">
                    <div className="row" style={{ gap: 10, alignItems: 'center' }}>
                      <input
                        type="checkbox"
                        checked={!!selectedResults[it.id]}
                        onChange={(e) => setSelectedResults((s) => ({ ...s, [it.id]: e.target.checked }))}
                        style={{ width: 16, height: 16 }}
                      />
                      <div className="tool-name">{it.toolName || it.toolId}</div>
                    </div>
                    <span className="badge">{fmt(it.createdAt)}</span>
                  </div>
                  <div className="muted" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
                    {(it.renderText || '').slice(0, 260)}
                    {it.renderText && it.renderText.length > 260 ? '…' : ''}
                  </div>
                  <div className="row" style={{ justifyContent: 'flex-end' }}>
                    <button
                      className="btn"
                      onClick={async () => {
                        try {
                          const data = await apiFetch<{ ok: boolean; filename: string; html: string }>('/api/exports/html', {
                            method: 'POST',
                            body: JSON.stringify({ resultId: it.id, title: `${it.toolName}结果`, question: '' })
                          });
                          downloadText(data.filename, data.html);
                          await load();
                        } catch {
                          setMsg('导出失败，请稍后重试');
                        }
                      }}
                    >
                      导出HTML
                    </button>
                    <button
                      className="btn danger"
                      onClick={async () => {
                        const ok = await confirmDelete({ title: '删除历史记录', message: '确认删除这条历史记录？', confirmText: '确认删除' });
                        if (!ok) return;
                        try {
                          await apiFetch(`/api/results/${encodeURIComponent(it.id)}`, { method: 'DELETE' });
                          await load();
                        } catch {
                          setMsg('删除失败，请稍后重试');
                        }
                      }}
                    >
                      删除
                    </button>
                  </div>
                </div>
              ))}
              {!results.length ? <div className="muted">暂无历史记录</div> : null}
            </div>
          ) : view === 'exports' ? (
            <div style={{ display: 'grid', gap: 12 }}>
              <div className="row" style={{ justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                <button
                  className="btn secondary"
                  type="button"
                  disabled={!exports.length}
                  onClick={() => {
                    const allSelected = exports.length > 0 && exports.every((it) => selectedExports[it.id]);
                    const next: Record<string, boolean> = {};
                    for (const it of exports) next[it.id] = !allSelected;
                    setSelectedExports(next);
                  }}
                >
                  {exports.length > 0 && exports.every((it) => selectedExports[it.id]) ? '取消全选' : '全选'}
                </button>
                <button
                  className="btn secondary"
                  type="button"
                  disabled={Object.values(selectedExports).filter(Boolean).length === 0}
                  onClick={() => setDataBulkOpen(true)}
                >
                  批量删除（{Object.values(selectedExports).filter(Boolean).length}）
                </button>
              </div>
              {exports.map((it) => (
                <div key={it.id} className="tool">
                  <div className="tool-top">
                    <div className="row" style={{ gap: 10, alignItems: 'center' }}>
                      <input
                        type="checkbox"
                        checked={!!selectedExports[it.id]}
                        onChange={(e) => setSelectedExports((s) => ({ ...s, [it.id]: e.target.checked }))}
                        style={{ width: 16, height: 16 }}
                      />
                      <div className="tool-name">{it.toolName || it.toolId}</div>
                    </div>
                    <span className="badge">{fmt(it.createdAt)}</span>
                  </div>
                  <div className="muted">Result：{it.resultId}</div>
                  <div className="row" style={{ justifyContent: 'flex-end' }}>
                    <button
                      className="btn"
                      onClick={async () => {
                        try {
                          const data = await apiFetch<{ ok: boolean; toolId: string; resultId: string; html: string }>(
                            `/api/exports/${encodeURIComponent(it.id)}`,
                            { method: 'GET' }
                          );
                          downloadText(`divination-${data.toolId}-${data.resultId}.html`, data.html);
                        } catch (e) {
                          if (e instanceof ApiError && e.status === 404) setMsg('记录不存在或已删除');
                          else setMsg('下载失败，请稍后重试');
                        }
                      }}
                    >
                      下载HTML
                    </button>
                    <button
                      className="btn danger"
                      onClick={async () => {
                        const ok = await confirmDelete({ title: '删除导出记录', message: '确认删除这条导出记录？', confirmText: '确认删除' });
                        if (!ok) return;
                        try {
                          await apiFetch(`/api/exports/${encodeURIComponent(it.id)}`, { method: 'DELETE' });
                          await load();
                        } catch {
                          setMsg('删除失败，请稍后重试');
                        }
                      }}
                    >
                      删除
                    </button>
                  </div>
                </div>
              ))}
              {!exports.length ? <div className="muted">暂无导出记录</div> : null}
            </div>
          ) : null}

          {view === 'results' || view === 'exports' ? (
            <div className="row" style={{ justifyContent: 'space-between', marginTop: 14 }}>
              <button className="btn secondary" onClick={() => setPage((p) => Math.max(1, p - 1))}>
                上一页
              </button>
              <span className="muted">第 {page} 页</span>
              <button className="btn secondary" onClick={() => setPage((p) => p + 1)}>
                下一页
              </button>
            </div>
          ) : null}

          <Modal open={dataBulkOpen} title="批量删除" onClose={() => setDataBulkOpen(false)}>
            <div className="muted" style={{ lineHeight: 1.8 }}>
              {view === 'results' ? '历史记录' : '导出记录'}批量删除不可恢复。
              <br />
              已选择{' '}
              {view === 'results' ? Object.values(selectedResults).filter(Boolean).length : Object.values(selectedExports).filter(Boolean).length} 条。
            </div>
            <div className="field" style={{ marginTop: 12 }}>
              <div className="muted">确认输入：DELETE</div>
              <input value={dataBulkConfirm} onChange={(e) => setDataBulkConfirm(e.target.value.slice(0, 20))} placeholder="DELETE" />
            </div>
            <div className="row" style={{ marginTop: 12, justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
              <button
                className="btn danger"
                type="button"
                disabled={
                  dataBulkRunning ||
                  dataBulkConfirm.trim().toUpperCase() !== 'DELETE' ||
                  (view === 'results'
                    ? Object.values(selectedResults).filter(Boolean).length === 0
                    : Object.values(selectedExports).filter(Boolean).length === 0)
                }
                onClick={async () => {
                  const ids =
                    view === 'results'
                      ? Object.entries(selectedResults)
                          .filter(([, v]) => v)
                          .map(([k]) => k)
                      : Object.entries(selectedExports)
                          .filter(([, v]) => v)
                          .map(([k]) => k);
                  if (!ids.length) return;
                  setDataBulkRunning(true);
                  setMsg(null);
                  try {
                    const url = view === 'results' ? '/api/results/bulk-delete' : '/api/exports/bulk-delete';
                    await apiFetch(url, { method: 'POST', body: JSON.stringify({ ids }) });
                    setMsg('批量删除成功');
                    setDataBulkOpen(false);
                    setDataBulkConfirm('');
                    if (view === 'results') setSelectedResults({});
                    else setSelectedExports({});
                    await load();
                  } catch {
                    setMsg('批量删除失败，请稍后重试');
                  } finally {
                    setDataBulkRunning(false);
                  }
                }}
              >
                {dataBulkRunning ? '删除中…' : '删除'}
              </button>
              <button className="btn secondary" type="button" onClick={() => setDataBulkOpen(false)}>
                取消
              </button>
            </div>
          </Modal>
        </div>
      </div>
    </div>
  );
}
